import { type FC, useContext, useEffect, useRef, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import useMeasure from 'react-use-measure';
import { Capacitor } from '@capacitor/core';
import { type TabBarItemsType } from '@common/types/components';
import { IntroContext } from '@components/Intro/context.ts';
import cn from 'classnames';

// import { a } from 'vitest/dist/chunks/suite.d.FvehnV49.js';
import { AppRoute } from '@/common/app/routes';
import { useMatchPath } from '@/common/hooks/match-path';
import { setCssVar } from '@/common/lib/cssVar';

// import { useIntroGuide } from '../Intro/intro-guide';
import { NavigateItem } from './NavigateItem/NavigateItem';
import { tabBarLinks } from './config';

import css from './styles.module.scss';

// The native app gets the iOS 26 tab bar: a glass capsule floating over the page
const isGlassTabBar = Capacitor.isNativePlatform();

const links: TabBarItemsType[] = [
  'Office',
  'Resources',
  'Materials',
  'Goods',
  'Stock',
];

const Tapbar: FC = () => {
  const [ref, rect] = useMeasure();
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const isTapBarInvisible = useMatchPath([
    AppRoute.ERRORPAGE,
    AppRoute.LOTTERY,
    AppRoute.LOTTERY + '/faq',
    AppRoute.LOTTERY + '/history',
    AppRoute.BINGO,
  ]);

  const cycleCountRef = useRef(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const indexRef = useRef(0); // Зберігаємо індекс у ref
  const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isOnboardingRef = useRef(false);

  const { activeStep, isFirstVisit } = useContext(IntroContext);
  const location = useLocation();

  //!!!!NEED TO REVIEW AND REWRITE!!!!

  // Функція для перезапуску таймера бездіяльності
  const resetInactivityTimer = (): void => {
    if (location.pathname !== AppRoute.MAIN) return;

    // Припиняємо активний цикл підсвічування, якщо він є
    if (intervalRef.current && !isOnboardingRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      setHighlightedIndex(null);
      // setCycleCount(0);
      indexRef.current = 0;
      cycleCountRef.current = 0;
    }

    // Очищаємо попередній таймер бездіяльності
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }
    // Запускаємо новий таймер бездіяльності, лише якщо на головній сторінці
    inactivityTimeoutRef.current = setTimeout(() => {
      if (intervalRef.current === null) {
        startHighlightCycle(1); // Для бездіяльності завжди 1 цикл
      }
    }, 5000); // 5 секунди
  };

  // Функція для запуску циклу підсвічування
  const startHighlightCycle = (cycles: number, isOnboarding = false): void => {
    if (intervalRef.current || location.pathname !== AppRoute.MAIN) return; // Вже запущено
    isOnboardingRef.current = isOnboarding;

    let index = 0; // локальний індекс
    let cycleCount = 0; // локальний лічильник циклів

    intervalRef.current = setInterval(() => {
      setHighlightedIndex(index);

      index++;

      if (index >= 4) {
        index = 0;
        cycleCount++;

        if (cycleCount >= cycles) {
          clearInterval(intervalRef.current!);
          intervalRef.current = null;
          isOnboardingRef.current = false;

          setTimeout(() => {
            setHighlightedIndex(null);
            resetInactivityTimer();
          }, 400);
        }
      }
    }, 400);
  };

  useEffect(() => {
    if (isGlassTabBar) {
      // The page runs under the capsule, so it only pads its end to scroll
      // the last row out from behind it
      setCssVar('--tabbar-height', '0px');
      setCssVar(
        '--tabbar-overlay',
        !isTapBarInvisible && rect.height
          ? `${window.innerHeight - rect.top + 8}px`
          : '0px',
      );
    } else {
      setCssVar('--tabbar-height', `${rect.height}px`);
    }

    setCssVar('--tabbar-top', `${rect.top}px`);
    setCssVar('--tabbar-bottom', `${rect.bottom}px`);
  }, [rect, isTapBarInvisible]);

  useEffect(() => {
    // Запускаємо анімацію, якщо activeStep === 27 і інтервал ще не створений
    if (activeStep === 27 && intervalRef.current === null) {
      startHighlightCycle(1, true);
    }
  }, [activeStep]);

  // Відстеження бездіяльності
  useEffect(() => {
    // Якщо НЕ на MAIN → стопаємо все
    if (location.pathname !== AppRoute.MAIN) {
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
        inactivityTimeoutRef.current = null;
      }
      setHighlightedIndex(null);

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      return;
    }

    if (isFirstVisit || [26, 27].includes(activeStep)) return;

    // ---- Далі: ми на MAIN ----
    const events = ['click', 'keydown', 'mousemove'];
    events.forEach((event) => {
      window.addEventListener(event, resetInactivityTimer);
    });

    resetInactivityTimer(); // 🚀 завжди запускаємо при вході на MAIN

    return (): void => {
      events.forEach((event) => {
        window.removeEventListener(event, resetInactivityTimer);
      });

      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
        inactivityTimeoutRef.current = null;
      }

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isFirstVisit, activeStep, location.pathname]);

  const items = links.map((label, index) => (
    <NavigateItem
      key={label}
      label={label}
      index={index}
      highlightedIndex={highlightedIndex}
      glass={isGlassTabBar}
    />
  ));

  if (isTapBarInvisible) return null;

  if (isGlassTabBar) {
    const activeIndex = links.findIndex((label) =>
      matchPath(
        { path: tabBarLinks[label], end: label === 'Office' },
        location.pathname,
      ),
    );

    return (
      <nav
        ref={ref}
        className={cn(css.glass, activeStep === 27 && 'z-[10000]')}
      >
        <div className={css.glassTrack}>
          <span
            className={css.glassLens}
            style={{
              transform: `translateX(${Math.max(activeIndex, 0) * 100}%)`,
              opacity: activeIndex < 0 ? 0 : 1,
            }}
          />
          {items}
        </div>
      </nav>
    );
  }

  return (
    <nav
      ref={ref}
      className={cn(
        css.tabbar,
        activeStep === 27 && 'absolute z-[10000] bottom-0 pb-[20px]',
      )}
    >
      {items}
    </nav>
  );
};

export default Tapbar;
